import { motion } from "framer-motion";
import { useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import { Button } from "./Button";
import { debounce } from "./debounce";
import { Feedback } from "./Feedback";
import useBarcodeScanner from "./hooks/useBarcodeScanner";
import { Receipt } from "./Receipt";
import {
    CheckoutSuccessSubStateKind,
    RootStateKind,
    useRootState
} from "./state/StateProvider";
import approveImage from "../../assets/approved.svg";
import { ActionKind, ScannedCode } from "../types/Action";

export const CheckoutSuccess = () => {
    const { t } = useTranslation();
    const { state, dispatch } = useRootState(RootStateKind.CheckoutSuccess);

    const [printed, setPrinted] = useState(false);

    const printReceipt = async () => {
        await dispatch({
            kind: ActionKind.PrintReceipt,
            orderId,
            kitchenOrderId: kitchenOrder?.id
        });
        setPrinted(true);
    };

    const debouncedPrint = useMemo(
        () => debounce(printReceipt, 500, "immediate"),
        []
    );

    const { subState, orderId, userId, kitchenOrder } = state;

    useBarcodeScanner({
        onScan: (scannedCode: ScannedCode) =>
            dispatch({ kind: ActionKind.Start, scannedCode })
    });

    const showReceipt =
        subState.kind === CheckoutSuccessSubStateKind.ReceiptLoading ||
        subState.kind === CheckoutSuccessSubStateKind.ReceiptSuccess;

    const GetReceiptButton = () => {
        return (
            <Button
                data-testid="getReceipt"
                onClick={() =>
                    dispatch({ kind: ActionKind.StartGetReceipt, orderId })
                }
                className="m-5 bg-white px-8 text-green-500"
            >
                {t("getReceipt")}
            </Button>
        );
    };
    const PrintButton = () => {
        return printed ? (
            <div data-testid="receiptPrinted" className="text-white">
                {t("receiptPrinted")}
            </div>
        ) : (
            <Button
                className="m-5 bg-white px-8 text-green-500"
                onClick={debouncedPrint}
            >
                {t("printReceipt")}
            </Button>
        );
    };
    const FinalizeButton = () => {
        return (
            <div className="mx-4">
                <Button
                    variant="primary"
                    data-testid="finalizeSession"
                    onClick={() =>
                        dispatch({ kind: ActionKind.FinalizeSession })
                    }
                    className="mb-4 w-full place-self-center px-3 py-4 text-center"
                >
                    {t("finalizeSession")}
                </Button>
            </div>
        );
    };

    const finalize = (
        <>
            <div
                data-testid="paymentSuccess"
                className="my-2 text-3xl font-semibold text-white"
            >
                {t("endTitle")}
            </div>
            <div className="mb-3 max-w-[30ch] text-center text-white">
                {t("endSubtitle")}
                {kitchenOrder?.displayId && (
                    <>
                        <div
                            data-testid="kitchen-order-number"
                            className="mx-8 my-4 rounded-xl bg-white p-6 text-center text-3xl text-green-500"
                        >
                            <div className="text-2xl opacity-75">
                                {t("orderNumber")}
                            </div>
                            <div className="text-4xl font-bold">
                                #{kitchenOrder.displayId}
                            </div>
                        </div>
                        <Trans i18nKey="takeReceipt" />
                    </>
                )}
            </div>

            {userId ? <PrintButton /> : <GetReceiptButton />}
        </>
    );

    return (
        <div className="flex h-full flex-col bg-primary-100">
            <div className="m-auto flex flex-col items-center">
                <div
                    className={`m-auto flex flex-col items-center rounded-modal ${
                        showReceipt ? "bg-white" : "bg-green-500"
                    } p-6`}
                >
                    <motion.img
                        className={`${showReceipt ? "hidden" : "visible"}`}
                        layout
                        src={approveImage}
                    />
                    {showReceipt ? <Receipt /> : finalize}
                </div>
                {userId && (
                    <div className="my-4 max-w-[30ch] text-center font-thin">
                        <Trans i18nKey="needReceipt" />
                    </div>
                )}
            </div>
            <FinalizeButton />
            <Feedback />
        </div>
    );
};
